import {
    emptyPrimaryContact,
    emptyPrimaryContactForm,
    PrimaryContact,
    PrimaryContactForm,
    PrimaryContactValidation
} from "./PrimaryContact";
import {EffectiveTaxRateType, emptyEffectiveTaxRate} from "./EffectiveTaxRateType";
import {
    NewClientProfileFormValidation,
    NewClientProfileValidationContainer
} from "../NewClientProfile/NewClientProfileSlice";
import {AdvisorDocumentMenus} from "../../Resources/models/AdviceLibrary";
import {ServiceTeamMember} from "./ServiceTeam";
import {SortType} from "../PartnerDashboard/partnerDashboardConstants";
import {ClientRelationshipGroupInfo} from "../ClientRelationshipGroup/ClientRelationshipGroupInformation";

export enum ProfileType {
    PROPOSAL = "PROPOSAL",
    PROFILE = "PROFILE"
}

export type PartnerDashboardProfile = {
    id: string,
    displayName: string,
    createdDate: string,
    lastModifiedDate: string|null,
    lastModifiedByName: string,
    archived: boolean,
    prospect: boolean,
    sample:boolean,
    lastMeetingEndDate: string|null,
    activePortfolioReserve: boolean,
    annualReportSentDate: string,
    activeClient: boolean,
    resourceCode: string|null,
    ordinal? : number,
    daysActive? : number,
    isNationalSample?: boolean
}

export enum ProfileType {
    CLIENT = "CLIENT",
    PROSPECT = "PROSPECT",
    SAMPLE = 'SAMPLE'
}

export enum DropdownItemNames {
    CLIENTS_WITH_CONDITIONS = "Clients with Conditions",
    LEGAL_AGREEMENTS_WP = "Legal Agreements without Profile",
    ACTIVE_CLIENT = 'Active Clients',
    ARCHIVED_CLIENTS = 'Archived Clients',
}


export type PartnerDashboardProfilesSortingInfo = {
    lanID: string
    profileType : ProfileType | string,
    dropdownItemName : DropdownItemNames,
    profileSortingInfo: ProfileSortingInfo[],
    sortColumnName: string,
    lastSortType: SortType
}

export type ProfileSortingInfo = {
    profileOrLegalAgreementId: string,
    ordinal: number
}

export type ProfileSettings = {
    alignmentStrategy: string,
    displayName: string,
    effectiveTaxRate: EffectiveTaxRateType,
    managementFees: string
    accreditedStatus: string,
    allocationType: string,
}


export type ProfileRequest = ProfileSettings & {
    primaryContact: PrimaryContactForm,
    secondaryContact?: PrimaryContactForm | null,
    clientRelationshipGroupInfo?: ClientRelationshipGroupInfo,
    resourceCode: string,
    archived: boolean
    prospect: boolean,
    sample:boolean,
    isNationalSample?: boolean,
    portfolioReserveTargetLength?: number,
    activePortfolioReserve: boolean,
    estimatedEstateTaxValue?: number,
    activeClient: boolean
}

export type ProspectRequest = {
    profile: ProfileRequest;
    serviceTeamMembers: ServiceTeamMember[];
}

export type SampleRequest = {
    profile: ProfileRequest;
    serviceTeamMembers: ServiceTeamMember[];
}

export type ProfileResponse = ProfileRequest & {
    id: string,
    createdDate: string,
    createdByName: string,
    lastModifiedDate: string | null,
    lastModifiedByName: string,
    primaryContact: PrimaryContact,
    owner: string,
    profileIdForProposal?: string | null
    portfolioReserveActivationDate: string | null
    type: ProfileType
    activeClient: boolean,
    archived: boolean,
    estimatedEstateTaxValue?: number,
    gpsIOFlag?: string
}

export type ProspectResponse = {
    profile: ProfileResponse;
    serviceTeamMembers: ServiceTeamMember[];
}

export type SampleResponse = {
    profile: ProfileResponse;
    serviceTeamMembers: ServiceTeamMember[];
}

export const NATIONAL_SAMPLE_TYPE = 'National Sample';
export const MY_SAMPLE_TYPE = 'My Sample';

const getSampleTypeValidation = (isNationalSample?: boolean) => {
    return isNationalSample === undefined ? 'Sample Type is required.' : null;
};

const getNewClientProfileFormValidation = (
    profile: ProfileRequest,
    firstNameLabel: string,
    sampleTypeValidation: string | null
) => {
    return {
        displayName: profile?.displayName ? null : "Display Name is required.",
        firstName: profile?.primaryContact?.firstName ? null : `${firstNameLabel} is required.`,
        lastName: profile?.primaryContact?.lastName ? null : "Last Name is required.",
        age: profile?.primaryContact?.age ? null : "Age is required.",
        personalPlanningHorizon: profile?.primaryContact?.personalPlanningHorizon ? null : "Personal Planning Horizon is required.",
        sampleType: sampleTypeValidation
    };
};

const getPrimaryContactValidation = (profile: ProfileRequest) => {
    return {
        firstName: profile?.primaryContact?.firstName ? null : "First Name is required.",
        lastName: profile?.primaryContact?.lastName ? null : "Last Name is required.",
        age: profile?.primaryContact?.age ? null : "Age is required.",
        personalPlanningHorizon: profile?.primaryContact?.personalPlanningHorizon ? null : "Personal Planning Horizon is required."
    };
};

const getProfileValidation = (
    profile: ProfileRequest,
    sampleTypeValidation: string | null
): ProfileRequestValidation => {
    const primaryContactValidation = getPrimaryContactValidation(profile);
    const primaryContactHasErrors = Object.values(primaryContactValidation).some((validation) => validation !== null);

    return {
        displayName: profile?.displayName ? null : "Display Name is required.",
        sampleType: sampleTypeValidation,
        primaryContact: primaryContactHasErrors ? primaryContactValidation : null
    };
};

export const getSampleTypeValue = (isNationalSample?: boolean) => {
    if (isNationalSample === undefined) {
        return undefined;
    }

    return isNationalSample ? NATIONAL_SAMPLE_TYPE : MY_SAMPLE_TYPE;
};

export const isNationalSampleSelection = (sampleType: string) => {
    return sampleType === NATIONAL_SAMPLE_TYPE;
};

export const validateProfileResponseContainer = (
    profile: ProfileRequest,
    firstNameLabel: string = "First Name",
    isPrimaryMember: boolean = true
): NewClientProfileValidationContainer => {
    const newClientProfileFormValidation = getNewClientProfileFormValidation(profile, firstNameLabel, null);

    return getProfileResponseContainerValidation(newClientProfileFormValidation, isPrimaryMember);
}

export const validateProfileResponseContainerWithRequiredSampleType = (
    profile: ProfileRequest,
    firstNameLabel: string = "First Name",
    isPrimaryMember: boolean = true
): NewClientProfileValidationContainer => {
    const newClientProfileFormValidation = getNewClientProfileFormValidation(
        profile,
        firstNameLabel,
        getSampleTypeValidation(profile?.isNationalSample)
    );

    return getProfileResponseContainerValidation(newClientProfileFormValidation, isPrimaryMember);
}

const getProfileResponseContainerValidation = (
    newClientProfileFormValidation: NonNullable<NewClientProfileFormValidation>,
    isPrimaryMember: boolean
): NewClientProfileValidationContainer => {

    const newClientProfileFormHasErrors = Object.values(newClientProfileFormValidation).some((validation) => validation !== null)

    if(isPrimaryMember)
    {
        return (
            {newClientProfileForm: newClientProfileFormHasErrors ? newClientProfileFormValidation : null}
        );
    }
    else {
        return (
            {newClientProfileForm: null , newClientProfileFormForSecondary: newClientProfileFormHasErrors ? newClientProfileFormValidation : null}
        );
    }

}
export const validateProfileResponse = (
    profile: ProfileRequest
): ProfileRequestValidation => {
    return getProfileValidation(profile, null);
}

export const validateProfileResponseWithRequiredSampleType = (
    profile: ProfileRequest
): ProfileRequestValidation => {
    return getProfileValidation(
        profile,
        getSampleTypeValidation(profile?.isNationalSample)
    );
}

export type ProfileRequestValidation = {
    displayName: null | string,
    sampleType?: null | string,
    primaryContact: null | PrimaryContactValidation,
}

export const emptyProfileSettings: ProfileSettings = {
    displayName: '',
    effectiveTaxRate: {...emptyEffectiveTaxRate},
    managementFees: '',
    accreditedStatus: '',
    allocationType: '',
    alignmentStrategy: ''
};

export const emptyProfileRequest: ProfileRequest = {
    displayName: '',
    primaryContact: {...emptyPrimaryContactForm},
    effectiveTaxRate: {...emptyEffectiveTaxRate},
    managementFees: '',
    accreditedStatus: '',
    allocationType: '',
    alignmentStrategy: '',
    resourceCode: '',
    archived: false,
    prospect: false,
    sample:false,
    isNationalSample: undefined,
    activePortfolioReserve: false,
    activeClient: true
}

export const emptyProfileResponse: ProfileResponse = {
    id: '',
    createdDate: '',
    lastModifiedDate: '',
    displayName: '',
    primaryContact: {...emptyPrimaryContact},
    effectiveTaxRate: {...emptyEffectiveTaxRate},
    managementFees: '',
    accreditedStatus: '',
    allocationType: '',
    alignmentStrategy: '',
    createdByName: '',
    lastModifiedByName: '',
    resourceCode: '',
    owner: '',
    archived: false,
    prospect: false,
    sample:false,
    isNationalSample: undefined,
    activePortfolioReserve: false,
    portfolioReserveActivationDate: '',
    type: ProfileType.PROFILE,
    activeClient: true
}

export const menuStructure: AdvisorDocumentMenus = {
    advisorDocumentSubMenus :[]
};
